/**
 * Fixture half of `acs_service/routers/operations.py` (U-16-f).
 *
 * This module mirrors the door's SEMANTICS, not its implementation: the resolver, the manifest store
 * and `job_machine` are Python and stay there. What is reproduced here is the five facts the console
 * has to behave against, each named with the server's own identifier:
 *
 *  1. **Locked slots are listed** (rule B-6, module docstring). Every operator slot comes back, with
 *     `available: false`, a `locked_reason` from §۴'s seven-word alphabet
 *     (`provisioning/manifest.py::LOCKED_REASONS`) and a `detail` naming the next step (VM-11).
 *  2. **`available` and the write share one owner** — `Resolution.write_permitted` — so a slot this
 *     module marks locked is refused by the fixture's write path with the same
 *     409 `capability-unproven` sentence the gate writes (`_capability_unproven`,
 *     `routers/operations.py:401-437`). One spelling of the rule, never a second one.
 *  3. **A write answers 202 `queued`** and mints a job. It is an acceptance and says nothing about
 *     the device (§۴.۳; invariant ۳ / ruling D6).
 *  4. **The typed confirmations are the server's strings**: `RUN <method> <model>` for the RPC door
 *     (`_run_confirm`, `:586`) and `WRITE-RAW <model> <count>` for the raw door
 *     (`_confirm_text`, `:772`). A wrong one is 409 `confirmation-required` carrying the exact text.
 *  5. **The raw door bypasses the per-operation vocabulary** — it takes paths, not slot ids — so its
 *     refusal set is the document-level one plus §۶.۲'s per-path check.
 *
 * Paths are read out of `fixtures/devices.ts::parametersFor` — the fleet's own declared table — never
 * invented here: a slot with no
 * path in the fleet's declared table comes back `no-binding`, which is the honest answer and the one
 * `noInventedPaths.test.ts` can keep.
 */
import type {
  OperationSlotItem,
  OperationValuePathEntry,
  OperationValueResponse,
  OperationsResponse,
  ParameterItem,
} from '../api/types';
import type { FixtureDevice } from './devices';
import { dataModelRootFor, parametersFor, rootOfPath } from './devices';
import { paginate } from './cursor';

/** §۴'s seven-word locked alphabet, copied from `provisioning/manifest.py::LOCKED_REASONS`. */
export const LOCKED_REASONS = [
  'no-binding',
  'no-witness',
  'not-writable',
  'blocked-path',
  'root-missing',
  'device-offline',
  'manifest-unresolved',
] as const;
export type LockedReason = (typeof LOCKED_REASONS)[number];

/** A slot this surface can answer for: one catalog row plus the path family it binds. */
interface FixtureSlot {
  readonly op: string;
  readonly kind: 'parameter' | 'action';
  readonly value_type: string;
  readonly secret: boolean;
  /** Catalog `writable: yes|conditional|no` — `no` never has a write door (`writable: false`). */
  readonly writable: boolean;
  /** Candidate parameter paths in preference order; the first the device actually declares wins. */
  readonly paths: readonly string[];
  /** The RPC an action row is, exactly as a manifest would declare it (`OperationSlotItem.rpc`). */
  readonly rpc?: string;
  /** Whether a witness covers this slot for the models the fixture fleet carries. */
  readonly witnessed: boolean;
}

const SLOTS: readonly FixtureSlot[] = [
  // Every candidate path is spelled on BOTH roots the fleet speaks (rule M0-2: the root comes off
  // the device, not off the brand), and a slot whose path the device does not declare comes back
  // `no-binding` — which is the honest answer, and the one `noInventedPaths.test.ts` keeps honest.
  {
    op: 'wifi.ssid.primary',
    kind: 'parameter',
    value_type: 'string',
    secret: false,
    writable: true,
    paths: [
      'Device.WiFi.SSID.1.SSID',
      'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID',
    ],
    witnessed: true,
  },
  {
    op: 'wifi.ssid.secondary',
    kind: 'parameter',
    value_type: 'string',
    secret: false,
    writable: true,
    paths: ['Device.WiFi.SSID.2.SSID'],
    witnessed: true,
  },
  {
    op: 'wifi.enable.primary',
    kind: 'parameter',
    value_type: 'boolean',
    secret: false,
    writable: true,
    paths: [
      'Device.WiFi.SSID.1.Enable',
      'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.Enable',
    ],
    witnessed: true,
  },
  {
    op: 'wifi.psk.primary',
    kind: 'parameter',
    value_type: 'string',
    secret: true,
    writable: true,
    paths: ['Device.WiFi.AccessPoint.1.Security.KeyPassed'],
    witnessed: true,
  },
  {
    op: 'wifi.txpower.primary',
    kind: 'parameter',
    value_type: 'int',
    secret: false,
    writable: true,
    paths: ['Device.WiFi.Radio.1.X_CT-COM_TxPower'],
    witnessed: false,
  },
  {
    op: 'wan.pppoe.username',
    kind: 'parameter',
    value_type: 'string',
    secret: false,
    writable: true,
    paths: [
      'Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.Username',
      'InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANPPPConnection.1.Username',
    ],
    witnessed: true,
  },
  {
    op: 'wan.pppoe.password',
    kind: 'parameter',
    value_type: 'string',
    secret: true,
    writable: true,
    paths: ['Device.WANDevice.1.WANConnectionDevice.2.WANPPPConnection.1.Password'],
    witnessed: true,
  },
  {
    op: 'wan.vlan.service',
    kind: 'parameter',
    value_type: 'int',
    secret: false,
    writable: true,
    paths: ['Device.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.VLANID'],
    witnessed: false,
  },
  {
    op: 'wan.enable',
    kind: 'parameter',
    value_type: 'boolean',
    secret: false,
    writable: true,
    paths: ['Device.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.Enable'],
    witnessed: false,
  },
  {
    op: 'device.software_version',
    kind: 'parameter',
    value_type: 'string',
    secret: false,
    writable: false,
    paths: [
      'Device.DeviceInfo.SoftwareVersion',
      'InternetGatewayDevice.DeviceInfo.SoftwareVersion',
    ],
    witnessed: true,
  },
  {
    op: 'device.uptime',
    kind: 'parameter',
    value_type: 'int',
    secret: false,
    writable: false,
    paths: ['Device.DeviceInfo.UpTime', 'InternetGatewayDevice.DeviceInfo.UpTime'],
    witnessed: true,
  },
  {
    op: 'device.serial_number',
    kind: 'parameter',
    value_type: 'string',
    secret: false,
    writable: false,
    paths: ['Device.DeviceInfo.SerialNumber'],
    witnessed: true,
  },
  {
    op: 'optical.rx_power',
    kind: 'parameter',
    value_type: 'float',
    secret: false,
    writable: false,
    paths: [],
    witnessed: true,
  },
  {
    op: 'optical.tx_power',
    kind: 'parameter',
    value_type: 'float',
    secret: false,
    writable: false,
    paths: [],
    witnessed: true,
  },
  {
    op: 'device.reboot',
    kind: 'action',
    value_type: 'action',
    secret: false,
    writable: true,
    paths: [],
    rpc: 'Reboot',
    witnessed: true,
  },
  {
    op: 'device.factory_reset',
    kind: 'action',
    value_type: 'action',
    secret: false,
    writable: true,
    paths: [],
    rpc: 'FactoryReset',
    witnessed: false,
  },
];

/** The two §۹ optical slots: no CWMP binding by design, read from the OLT over SNMP. */
const OPTICAL: Record<string, 'rx' | 'tx'> = {
  'optical.rx_power': 'rx',
  'optical.tx_power': 'tx',
};

/** §۶.۱'s layer ۲ word, and §۴.۱۰'s RPC word — both verbatim from `routers/operations.py`. */
export const RAW_CONFIRM_WORD = 'WRITE-RAW';
export const RUN_CONFIRM_WORD = 'RUN';

export function rawConfirmText(model: string, count: number): string {
  return `${RAW_CONFIRM_WORD} ${model} ${count}`;
}

export function runConfirmText(rpc: string, model: string): string {
  return `${RUN_CONFIRM_WORD} ${rpc} ${model}`;
}

/** The device as the gate names it: brand, model, and the build a witness has to cover (`_device_words`). */
export function deviceWords(device: FixtureDevice): string {
  const firmware = device.software_version;
  return (
    `${device.manufacturer} ${device.model}` +
    (firmware ? ` firmware ${firmware}` : ' (no firmware reported)')
  );
}

/**
 * The root a bound path sits on, in the DOOR's spelling: the short name (`tr098` / `tr181`), never
 * the literal prefix. `OperationSlotItem.root` is the manifest's declared root (`models.py:1069`,
 * read off the binding by `resolver.py:602`; the manifests spell `"root": "tr098"`), and the door's
 * `?root=` filter accepts exactly that alphabet — a prefix is a 422 there. Publishing the prefix
 * here once made one response document contradict itself: `manifest.data_model_root` already spoke
 * the short name while the rows spoke prefixes, and the response filter compared the two.
 */
function slotRoot(path: string | null): string {
  if (!path) return '';
  const root = rootOfPath(path);
  return root === null ? '' : root;
}

/** The device's declared table for this session (`fixtures/devices.ts::parametersFor`). */
function tableOf(device: FixtureDevice): Map<string, ParameterItem> {
  return new Map(parametersFor(device).map((item) => [item.path, item]));
}

/** The path this device actually answers for a slot, or null when nothing binds it. */
function boundPath(device: FixtureDevice, slot: FixtureSlot): string | null {
  const table = tableOf(device);
  return slot.paths.find((path) => table.has(path)) ?? null;
}

/** §۴.۴'s refusal pair: a reason from §۴'s closed seven-word alphabet plus the next step (VM-11). */
function lockFor(
  device: FixtureDevice,
  slot: FixtureSlot,
  path: string | null,
): { locked_reason: LockedReason; detail: string } | null {
  if (slot.kind !== 'action' && !path) {
    return {
      locked_reason: 'no-binding',
      detail:
        `no path of ${deviceWords(device)} carries a binding for ${slot.op}; the way in is ` +
        'POST /ops/v1/devices/{id}:discover for this model+firmware, then promote the binding ' +
        '(§۷.۴). There is no force flag in this body.',
    };
  }
  if (!slot.witnessed) {
    return {
      locked_reason: 'no-witness',
      detail:
        `the binding exists but no witness covers ${slot.op} on ${deviceWords(device)}; ` +
        'POST /ops/v1/devices/{id}:discover for this model+firmware, then promote the binding (§۷.۴).',
    };
  }
  if (!slot.writable) {
    return {
      locked_reason: 'not-writable',
      detail:
        `${slot.op} is read-only in the catalog: it has a value to show and no write door, ` +
        'so this row is never an action.',
    };
  }
  if (!device.online) {
    return {
      locked_reason: 'device-offline',
      detail:
        `${deviceWords(device)} is offline; a write needs a session to carry the ` +
        'SetParameterValues and there is none (fail-closed). This is the one refusal a retry can clear.',
    };
  }
  return null;
}

/**
 * One catalog row as the gate sees it for this device — locked rows included (rule B-6).
 *
 * `available` is the gate's write verdict and `writable` is `true` only where it is, exactly as
 * `_slot_item` builds them (`routers/operations.py:88-100`): a read-only sensor never looks like an
 * action, and a locked row carries its reason instead of disappearing.
 */
export function slotItem(device: FixtureDevice, slot: FixtureSlot): OperationSlotItem {
  const path = slot.kind === 'action' ? null : boundPath(device, slot);
  const locked = lockFor(device, slot, path);
  const captured = path ? (tableOf(device).get(path)?.captured_at ?? null) : null;
  return {
    op: slot.op,
    kind: slot.kind,
    value_type: slot.value_type,
    secret: slot.secret,
    root: slotRoot(path),
    available: locked === null,
    writable: locked === null ? true : null,
    // A row the gate refuses for want of a witness still names the provenance it DID see — that is
    // what `_capability_unproven` prints, and the console renders the gate's own sentence.
    provenance:
      locked === null ? 'witnessed' : locked.locked_reason === 'no-witness' ? 'declared' : null,
    locked_reason: locked ? locked.locked_reason : null,
    path,
    rpc: slot.kind === 'action' ? (slot.rpc ?? null) : null,
    captured_at: captured,
    detail: locked
      ? locked.detail
      : `${slot.op} is witnessed for ${deviceWords(device)}; the write door is PUT …/operations/${slot.op}.`,
  };
}

/** Every operator slot of the fixture catalog, in catalog order — locked rows included. */
export function fixtureSlotsFor(device: FixtureDevice): OperationSlotItem[] {
  return SLOTS.map((slot) => slotItem(device, slot));
}

function findSlot(op: string): FixtureSlot | null {
  return SLOTS.find((slot) => slot.op === op) ?? null;
}

/** Is this op even a row of the catalog? The door answers 404 `operation-unknown` when it is not. */
export function isKnownOperation(op: string): boolean {
  return findSlot(op) !== null;
}

/** The document that answered for this device — a fixture id, shaped like a manifest id. */
function fixtureManifestId(device: FixtureDevice): string {
  return `fx-${device.model.replace(/\s+/g, '-').toLowerCase()}`;
}

/**
 * `GET …/operations` — the door's filters and cursor paging.
 *
 * `root` and `available` are applied the server's way: an unknown `root` is refused by the caller
 * (422 `reference-invalid`), never folded into «this device has no operations».
 */
export function fixtureOperationsResponse(
  device: FixtureDevice,
  query: { root?: string; available?: boolean; cursor?: string; limit?: number },
): OperationsResponse {
  let items = fixtureSlotsFor(device);
  if (query.root) items = items.filter((item) => item.root === query.root);
  if (typeof query.available === 'boolean') {
    items = items.filter((item) => item.available === query.available);
  }
  const page = paginate(items, query.cursor, query.limit ?? 50);
  return {
    items: page.items,
    manifest: {
      manifest_id: fixtureManifestId(device),
      manifest_version: 1,
      matched_on: 'model',
      // Rung ۳ is what a model-matched document answers with; rung ۶ would say `manifest_id: null`
      // and lock every row `manifest-unresolved` (rule Rung-۶-is-a-state).
      ladder_rung: 3,
      data_model_root: dataModelRootFor(device),
      provenance_min: 'witnessed',
    },
    next_cursor: page.next_cursor,
  };
}

/**
 * The value of one slot for one device, read off the fleet's own parameter table (§۴.۲).
 *
 * `source` is the field this exists to carry: `snapshot` for a cached CWMP value, `olt-snmp` for the
 * two §۹ optical slots (no CWMP binding by design), `none` when there is nothing to show. A value
 * here never claims a CWMP round trip happened — that executor is M3 and the door says so.
 */
export function fixtureOperationValue(device: FixtureDevice, op: string): OperationValueResponse {
  const slot = findSlot(op);
  if (!slot) throw new Error(`unknown operation ${op}`);
  const item = slotItem(device, slot);
  const path = slot.kind === 'action' ? null : boundPath(device, slot);
  const optical = OPTICAL[slot.op];
  const base = {
    op: slot.op,
    kind: slot.kind,
    value_type: slot.value_type,
    secret: slot.secret,
    // §۴.۲: a cached answer served for a device that is not reachable now is `stale`, not `fresh`.
    stale: !device.online,
    manifest_id: fixtureManifestId(device),
    manifest_version: 1,
    available: item.available,
    locked_reason: item.locked_reason,
    provenance: item.provenance,
    value_length: null as number | null,
    paths: [] as OperationValuePathEntry[],
  };
  if (optical) {
    const value = optical === 'rx' ? device.rxDbm : device.txDbm;
    const why = 'read from the OLT over SNMP (§۹: an optical slot has no CWMP binding by design)';
    return {
      ...base,
      value,
      source: value === null ? 'none' : 'olt-snmp',
      captured_at: value === null ? null : device.last_inform_at,
      detail: value === null ? `${why}; no sample returned` : why,
    };
  }
  if (slot.kind === 'action') {
    // §۲.۴'s B-4: an action row has no value to read, so the fixture hands back none.
    return {
      ...base,
      value: null,
      source: 'none',
      captured_at: null,
      detail: `${slot.op} is an action row: it is run with POST …/operations/${slot.op}:run, never read as a value.`,
    };
  }
  if (!path)
    return { ...base, value: null, source: 'none', captured_at: null, detail: item.detail };
  const row = tableOf(device).get(path);
  const value = row?.value ?? null;
  // B-5: a secret's content never leaves the façade; only its length is published.
  const masked = slot.secret;
  return {
    ...base,
    value: masked ? null : value,
    value_length: masked ? String(value ?? '').length : null,
    source: 'snapshot',
    captured_at: row?.captured_at ?? null,
    paths: [
      {
        root: slotRoot(path),
        path,
        value: masked ? null : value,
        captured_at: row?.captured_at ?? null,
        fault_code: null,
      },
    ],
    detail: masked
      ? 'the slot is secret: the value is masked server-side and only its length travels'
      : 'read from the façade snapshot; no value here claims a CWMP round trip (that is M3)',
  };
}

/** §۴.۴'s refusal sentence, in the gate's own words (`_capability_unproven`, `operations.py:401-437`). */
export function capabilityUnprovenDetail(
  op: string,
  item: OperationSlotItem,
  device: FixtureDevice,
): string {
  const words = deviceWords(device);
  const observed = item.provenance;
  const lead = observed
    ? `${JSON.stringify(op)} cannot be written on ${words}: provenance ${JSON.stringify(observed)} clears the floor, so the refusal is a different fact`
    : `no witness recorded for ${words}; binding provenance is ${'none recorded'}, write gate requires >= 'witnessed'`;
  const reason = item.locked_reason ?? 'no-binding';
  return `${lead}. ${reason}: ${item.detail}`;
}
